'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { CheckCircle, ArrowRight, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/layout/header'
import { Footer } from '@/components/layout/footer'
import { createClient } from '@/lib/supabase/client'
import posthog from 'posthog-js'
import { analytics } from '@/lib/analytics'

const FEATURE_FLAG = 'pricing-annual-framing'

export default function PricingPage() {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null)
  const [pricingVariant, setPricingVariant] = useState<'control' | 'annual'>('control')
  const router = useRouter()

  useEffect(() => {
    const cleanup = posthog.onFeatureFlags(() => {
      const flag = posthog.getFeatureFlag(FEATURE_FLAG)
      const variant: 'control' | 'annual' = flag === 'annual' ? 'annual' : 'control'
      setPricingVariant(variant)
      if (variant === 'annual') {
        setBillingPeriod('yearly')
      }
      analytics.pricingPageViewed({ variant })
    })
    return cleanup
  }, [])

  async function handlePaidPlan(planKey: 'normal' | 'pro') {
    const supabase = createClient()
    const { data: { user } } = await supabase.auth.getUser()

    if (!user) {
      router.push(`/signup?plan=${planKey}`)
      return
    }

    analytics.upgradeInitiated(planKey, pricingVariant)
    setLoadingPlan(planKey)
    try {
      const res = await fetch('/api/billing/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planKey }),
      })
      const data = await res.json()
      if (data.url) {
        window.location.href = data.url
      }
    } finally {
      setLoadingPlan(null)
    }
  }

  const plans = [
    {
      name: 'Free',
      planKey: null as null,
      monthlyPrice: '0',
      annualPrice: '0',
      description: 'Perfect for exploring',
      cta: 'Get started',
      href: '/signup',
      features: [
        { text: '5 free sessions per month', included: true },
        { text: 'Access to all guides and news', included: true },
        { text: 'Newsletter subscription', included: true },
        { text: 'Unlimited sessions', included: false },
        { text: 'Priority support', included: false },
        { text: 'Advanced personalization', included: false },
      ],
      highlight: false,
    },
    {
      name: 'Normal',
      planKey: 'normal' as 'normal',
      monthlyPrice: '19',
      annualPrice: '15',
      annualBilled: '182',
      description: 'For regular users',
      cta: 'Subscribe',
      href: null,
      badge: 'Recommended',
      features: [
        { text: '25 sessions/month', included: true },
        { text: 'Access to all guides and news', included: true },
        { text: 'Priority support', included: true },
        { text: 'Advanced personalization', included: true },
        { text: 'Newsletter subscription', included: true },
        { text: 'Offline access (coming soon)', included: true },
      ],
      highlight: true,
    },
    {
      name: 'Pro',
      planKey: 'pro' as 'pro',
      monthlyPrice: '49',
      annualPrice: '39',
      annualBilled: '468',
      description: 'For power users',
      cta: 'Subscribe',
      href: null,
      features: [
        { text: 'Everything in Normal', included: true },
        { text: 'Expert consultant access', included: true },
        { text: 'Monthly strategy calls', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'API access', included: true },
        { text: 'Dedicated support', included: true },
      ],
      highlight: false,
    },
  ]

  const faqs = [
    {
      q: 'Can I change plans anytime?',
      a: 'Yes. You can upgrade or downgrade anytime. Changes take effect at your next billing cycle.',
    },
    {
      q: 'Do you offer annual discounts?',
      a: 'Yes. Pay annually and save 20%. Choose the annual billing option at checkout.',
    },
    {
      q: 'Is there a free trial?',
      a: 'The Free plan gives you 5 sessions per month — no credit card required. Upgrade to Normal or Pro anytime when you\'re ready for more.',
    },
    {
      q: "What's your refund policy?",
      a: 'We offer a 30-day money-back guarantee on paid plans. Just ask, no questions.',
    },
    {
      q: 'Can I cancel anytime?',
      a: "Absolutely. Cancel anytime before your next billing date. We won't try to convince you to stay.",
    },
    {
      q: 'Do you offer team/enterprise plans?',
      a: 'Yes. Contact us at sales@thenormal.ai for custom team and enterprise pricing.',
    },
  ]

  return (
    <div className="min-h-screen bg-background text-charcoal">
      <Header />

      {/* Header Section */}
      <section className="py-20 sm:py-28 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Simple, transparent pricing</h1>
          <p className="text-lg sm:text-xl text-warm-gray mb-8">
            No surprises. No hidden fees. Start free, upgrade when you're ready.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-8">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-md font-semibold transition-all ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-charcoal shadow-sm'
                  : 'text-warm-gray hover:text-charcoal'
              }`}
            >
              Pay monthly
            </button>
            {pricingVariant === 'annual' ? (
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-2 rounded-md font-semibold transition-all ${
                  billingPeriod === 'yearly'
                    ? 'bg-white text-charcoal shadow-sm'
                    : 'text-warm-gray hover:text-charcoal'
                }`}
              >
                Pay yearly{' '}
                <span className="text-xs font-normal text-green-600 ml-1">Save 20%</span>
              </button>
            ) : (
              <button
                disabled
                className="px-6 py-2 rounded-md font-semibold text-warm-gray opacity-50 cursor-not-allowed"
                title="Annual billing coming soon"
              >
                Pay yearly <span className="text-xs font-normal">(coming soon)</span>
              </button>
            )}
          </div>
          {pricingVariant === 'annual' && billingPeriod === 'yearly' && (
            <p className="text-sm text-green-700 font-medium mb-2">
              Annual billing active — you save 20% vs monthly
            </p>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {plans.map((plan, i) => {
              const displayPrice = billingPeriod === 'yearly' ? plan.annualPrice : plan.monthlyPrice
              return (
                <div
                  key={i}
                  className={`relative rounded-lg border-2 transition-all ${
                    plan.highlight
                      ? 'border-primary bg-white shadow-lg scale-105'
                      : 'border-gray-200 bg-white hover:border-primary/50'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="px-4 py-1 bg-primary text-white text-sm font-bold rounded-full">
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Name */}
                    <h3 className="text-2xl font-bold text-charcoal mb-2">{plan.name}</h3>
                    <p className="text-warm-gray text-sm mb-6">{plan.description}</p>

                    {/* Price */}
                    <div className="mb-8">
                      <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-bold text-primary">${displayPrice}</span>
                        {plan.monthlyPrice !== '0' && (
                          <span className="text-warm-gray">/mo</span>
                        )}
                      </div>
                      {plan.monthlyPrice === '0' && (
                        <p className="text-sm text-warm-gray mt-2">Forever free</p>
                      )}
                      {billingPeriod === 'yearly' && plan.annualBilled && (
                        <p className="text-sm text-warm-gray mt-2">
                          Billed ${plan.annualBilled}/year
                        </p>
                      )}
                    </div>

                    {/* CTA */}
                    <div className="mb-8">
                      {plan.planKey ? (
                        <Button
                          onClick={() => handlePaidPlan(plan.planKey!)}
                          disabled={loadingPlan === plan.planKey}
                          className={`w-full py-3 ${
                            plan.highlight
                              ? 'bg-primary hover:bg-primary-700 text-white'
                              : 'border border-gray-300 text-charcoal hover:bg-gray-50'
                          }`}
                          variant={plan.highlight ? 'default' : 'outline'}
                        >
                          {loadingPlan === plan.planKey ? 'Redirecting…' : plan.cta}
                        </Button>
                      ) : (
                        <Link href={plan.href!} className="block">
                          <Button
                            className="w-full py-3 border border-gray-300 text-charcoal hover:bg-gray-50"
                            variant="outline"
                          >
                            {plan.cta}
                          </Button>
                        </Link>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-4">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle
                            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                              feature.included ? 'text-success' : 'text-gray-300'
                            }`}
                          />
                          <span className={feature.included ? 'text-charcoal' : 'text-warm-gray'}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Feature Comparison Table */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 overflow-x-auto mb-12">
            <h3 className="text-xl font-bold text-charcoal mb-6">What's included in all plans</h3>
            <div className="space-y-4">
              {[
                { feature: 'Access to all guides and news', included: true },
                { feature: 'Newsletter subscription', included: true },
                { feature: 'Community support', included: true },
                { feature: 'Can be cancelled anytime', included: true },
                { feature: '30-day money-back guarantee', included: true },
                { feature: 'Priority email support', included: false, fromPlan: 'Normal' },
                { feature: 'Expert consultant access', included: false, fromPlan: 'Pro' },
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                  <span className="text-charcoal">{item.feature}</span>
                  {item.included ? (
                    <CheckCircle className="w-5 h-5 text-success" />
                  ) : (
                    <span className="text-xs text-warm-gray">{item.fromPlan || 'Not included'}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 sm:py-28 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-charcoal mb-12 text-center">Questions?</h2>

          <div className="space-y-6">
            {faqs.map((faq, i) => (
              <details key={i} className="bg-gray-50 rounded-lg border border-gray-200 p-6 cursor-pointer group">
                <summary className="flex items-center justify-between font-semibold text-charcoal list-none">
                  {faq.q}
                  <ChevronDown className="w-5 h-5 text-primary flex-shrink-0 group-open:rotate-180 transition-transform" />
                </summary>
                <p className="text-warm-gray mt-4 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-warm-gray mb-4">Still have questions?</p>
            <Link href="mailto:support@thenormal.ai" className="text-primary font-semibold hover:underline">
              Get in touch
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 sm:py-28 bg-primary/5 border-t border-primary/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-charcoal mb-4">Ready to get started?</h2>
          <p className="text-lg text-warm-gray mb-8">
            Start with 5 free sessions. No credit card required.
          </p>
          <Link href="/signup">
            <Button className="bg-primary hover:bg-primary-700 text-white px-8 py-6 text-lg flex items-center gap-2 mx-auto">
              Get Help — Free
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}
